import React from 'react';
import { Link, NavLink, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { PageContainer } from '@urbantxt/layout';
import clsx from 'clsx';
import { Badge } from 'components/ui';
import { FormLockProvider } from 'components/ui/FormLock';
import Loader from 'components/Elements/Loader/Loader';
import { UserSession } from 'types';
import useUserStore from 'store/useUserStore';
import reopenSclaApplication from 'features/scla/api/reopenSclaApplication';
import { getSclaAppHomePath, withSclaCohortYear } from 'utils/navigation';
import { sclaShowsTxterBadge } from 'utils/sclaApplicationKind';
import { SclaApplication } from '../types';
import useSclaApplication from '../hooks/useSclaApplication';
import useSclaApplicationYears from '../hooks/useSclaApplicationYears';
import {
  resetSclaSaveStatus,
  sclaSaveStatusLabel,
  useSclaSaveStatus,
} from '../hooks/useSclaSaveStatus';
import ApplicationNotStarted from '../components/ApplicationNotStarted';
import SclaStepMark from '../components/steps/SclaStepMark';
import { SCLA_APPLICATION_SECTIONS } from '../config/sections';
import {
  sclaStepState,
} from '../utils/sectionStatus';
import styles from './SclaAppLayout.module.scss';

export interface SclaApplicationOutletContext {
  application: SclaApplication;
  setApplication: (app: SclaApplication) => void;
  readOnly: boolean;
  yearClosed: boolean;
  cohortYear: string;
}

function SclaSaveStatusText() {
  const { phase, savedAt } = useSclaSaveStatus();
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 15000);
    return () => window.clearInterval(id);
  }, []);

  const saved = Boolean(savedAt) && phase !== 'saving' && phase !== 'error';
  const tone = (() => {
    if (phase === 'error') return styles.savedError;
    if (phase === 'saving') return styles.savedBusy;
    if (saved) return styles.savedOk;
    return undefined;
  })();

  return (
    <p className={clsx(styles.saved, tone)}>
      {sclaSaveStatusLabel(phase, savedAt, now)}
    </p>
  );
}

function stepDetail(state: ReturnType<typeof sclaStepState>): string {
  if (state === 'started') return ', started, not complete';
  if (state === 'complete') return ', complete';
  if (state === 'optional') return ', optional';
  return '';
}

export default function SclaAppLayout({ user }: { user: UserSession }) {
  const sessionUser = useUserStore(state => state.user);
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const requestedYear = searchParams.get('cohortYear');
  const { data, error, isLoading } = useSclaApplicationYears();
  const currentCohortYear = data?.currentCohortYear ?? '';
  const cohortYear = requestedYear || currentCohortYear;
  const yearStatus = data?.years.find(year => year.cohortYear === cohortYear)
    ?.status;
  const {
    application,
    isDoneLoading,
    isStarting,
    setApplication,
    startApplication,
  } = useSclaApplication(
    user._id,
    cohortYear,
    yearStatus === 'open',
    !requestedYear || requestedYear === currentCohortYear,
  );

  React.useEffect(() => () => resetSclaSaveStatus(), []);

  if (isLoading || (cohortYear && !isDoneLoading)) {
    return <Loader isVisible fullscreen={false} theme="light" />;
  }

  if (error || !cohortYear) {
    return (
      <PageContainer>
        <p>The application calendar could not be loaded.</p>
      </PageContainer>
    );
  }

  if (application === null || application.cohortYear !== cohortYear) {
    return (
      <PageContainer>
        {yearStatus === 'open' ? (
          <ApplicationNotStarted
            onStart={startApplication}
            isLoading={isStarting}
          />
        ) : (
          <p>This application year is not open.</p>
        )}
      </PageContainer>
    );
  }

  const submitted = Boolean(application.submissionTimestamp)
    || application.appStatus === 'complete';
  const yearClosed = yearStatus !== 'open';
  const readOnly = yearClosed || submitted;
  const fieldsLocked = yearClosed || (submitted && !pathname.endsWith('/transcripts'));
  const showTxter = sclaShowsTxterBadge(
    application,
    sessionUser?.type || user.type,
  );
  const stepIndex = SCLA_APPLICATION_SECTIONS.findIndex(section =>
    pathname.endsWith(`/${section.id}`),
  );
  const current = stepIndex >= 0 ? SCLA_APPLICATION_SECTIONS[stepIndex] : null;

  return (
    <PageContainer>
      <div className={styles.shell}>
        <div className={styles.top}>
          <Link className={styles.exit} to={getSclaAppHomePath()}>
            ← Save and exit to application home
          </Link>
          {submitted ? (
            <p className={clsx(styles.saved, styles.savedOk)}>Submitted</p>
          ) : (
            <SclaSaveStatusText />
          )}
          {submitted && process.env.NODE_ENV === 'development' ? (
            <button
              type="button"
              className={styles.devReset}
              onClick={() => {
                reopenSclaApplication(cohortYear)
                  .then(updated => {
                    setApplication(updated);
                    resetSclaSaveStatus();
                  })
                  .catch(() => undefined);
              }}
            >
              Reset to unsubmitted
            </button>
          ) : null}
        </div>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>
            {`${cohortYear} SCLA Application`}
            {showTxter ? <Badge tone="brand">TXTer</Badge> : null}
          </h1>
        </div>
        <div className={styles.body}>
          <div className={styles.stepNav}>
            <ol className={styles.steps}>
              {SCLA_APPLICATION_SECTIONS.map((section, index) => {
                const state = sclaStepState(application, section.id);
                const to = readOnly
                  ? withSclaCohortYear(section.to(), cohortYear)
                  : section.to();
                return (
                  <li key={section.id}>
                    <NavLink
                      to={to}
                      className={({ isActive }) =>
                        clsx(styles.stepLink, isActive && styles.stepOn)
                      }
                    >
                      <SclaStepMark state={state} index={index} />
                      <span>
                        <b>{section.navTitle}</b>
                        <small>{section.subtitle}</small>
                      </span>
                    </NavLink>
                  </li>
                );
              })}
            </ol>
          </div>
          <div className={styles.main}>
            {current ? (
              <p className={styles.mobileStep}>
                {`Step ${stepIndex + 1} of ${SCLA_APPLICATION_SECTIONS.length} · ${current.navTitle}`}
              </p>
            ) : null}
            {current ? (
              <nav className={styles.mobileDots} aria-label="Application steps">
                {SCLA_APPLICATION_SECTIONS.map((section, index) => {
                  const state = sclaStepState(application, section.id);
                  const to = readOnly
                    ? withSclaCohortYear(section.to(), cohortYear)
                    : section.to();
                  const detail = stepDetail(state);
                  return (
                    <NavLink
                      key={section.id}
                      to={to}
                      className={({ isActive }) =>
                        clsx(styles.mobileDot, isActive && styles.mobileDotOn)
                      }
                      aria-label={`${section.navTitle}${detail}`}
                    >
                      <SclaStepMark state={state} index={index} compact />
                    </NavLink>
                  );
                })}
              </nav>
            ) : null}
            {yearClosed && !submitted ? (
              <p className={styles.notice}>Read-only. This application year is closed.</p>
            ) : null}
            <FormLockProvider locked={fieldsLocked}>
              <Outlet
                context={{ application, setApplication, readOnly, yearClosed, cohortYear }}
              />
            </FormLockProvider>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
